/**
 * API client for NILE RapidConnect Checkout
 */

import * as Sentry from "@sentry/react";
import type {
  ApiResponse,
  Cart,
  ShippingOption,
  CreateOrderRequest,
  CreateOrderResponse,
  OrderWithItems,
  UpdateCartRequest,
  RemoveFromCartRequest,
} from "@/types";
import { config, getApiUrl } from "@/lib/config";
import { getSessionToken } from "@/lib/utils";
import {
  isDevCartEnabled,
  getOrCreateDevCart,
  updateDevCartItem,
  removeDevCartItem,
} from "./dev-cart";

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Make authenticated API request
 * Handles non-JSON responses and network errors
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const sessionToken = getSessionToken();

  if (!sessionToken) {
    throw new ApiError("No session token found", "NO_SESSION_TOKEN", 401);
  }

  const url = getApiUrl(endpoint);

  let response: Response;
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      [config.sessionTokenHeader]: sessionToken,
    };

    // Add Supabase anon key if configured (required for edge functions)
    if (config.supabaseAnonKey) {
      headers["Authorization"] = `Bearer ${config.supabaseAnonKey}`;
    }

    response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
  } catch (error) {
    const apiError = new ApiError(
      error instanceof Error ? error.message : "Network request failed",
      "NETWORK_ERROR",
      undefined,
      error
    );
    // Only capture network errors that aren't expected (like offline)
    if (error instanceof TypeError && error.message.includes("fetch")) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(apiError, {
          tags: { api_endpoint: endpoint, error_type: "network" },
          extra: { url },
        });
      }
    }
    throw apiError;
  }

  // Handle non-2xx responses
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      const error = new ApiError(
        `API request failed: ${response.statusText}`,
        "NETWORK_ERROR",
        response.status
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { api_endpoint: endpoint, http_status: response.status },
          extra: { url, statusText: response.statusText },
        });
      }
      throw error;
    }

    if (errorData && !errorData.success) {
      const error = new ApiError(
        errorData.error?.message || "An error occurred",
        errorData.error?.code,
        response.status,
        errorData.error?.details
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: {
            api_endpoint: endpoint,
            error_code: errorData.error?.code,
            http_status: response.status,
          },
          extra: { url, errorData },
        });
      }
      throw error;
    }

    const error = new ApiError("An unexpected error occurred", "UNKNOWN_ERROR", response.status);
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { api_endpoint: endpoint, http_status: response.status },
        extra: { url, errorData },
      });
    }
    throw error;
  }

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch (error) {
    const parseError = new ApiError("Invalid response from server", "PARSE_ERROR", response.status);
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(parseError, {
        tags: { api_endpoint: endpoint, error_type: "parse" },
        extra: { url, originalError: error instanceof Error ? error.message : String(error) },
      });
    }
    throw parseError;
  }

  if (!data.success) {
    const apiError = new ApiError(
      data.error.message,
      data.error.code,
      response.status,
      data.error.details
    );
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(apiError, {
        tags: { api_endpoint: endpoint, error_code: data.error.code, http_status: response.status },
        extra: { url, errorData: data.error },
      });
    }
    throw apiError;
  }

  return data.data;
}

/**
 * Transform API cart response to match expected SPA format
 */
function transformCartResponse(cart: Cart): Cart {
  return {
    ...cart,
    items: cart.items.map((item) => {
      const apiItem = item as Cart["items"][0] & { price_cents?: number };
      return {
        ...item,
        unit_price_cents: apiItem.price_cents ?? item.unit_price_cents,
      };
    }),
  };
}

/**
 * Cart API
 */
export const cartApi = {
  /**
   * Get cart contents
   */
  getCart: async (sessionId: string): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return getOrCreateDevCart(sessionId);
    }
    const cart = await apiRequest<Cart>(`/cart/${sessionId}`);
    return transformCartResponse(cart);
  },

  /**
   * Update cart item quantity
   */
  updateItem: async (request: UpdateCartRequest): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return updateDevCartItem(request);
    }
    const cart = await apiRequest<Cart>("/cart/update", {
      method: "PUT",
      body: JSON.stringify(request),
    });
    return transformCartResponse(cart);
  },

  /**
   * Remove item from cart
   */
  removeItem: async (request: RemoveFromCartRequest): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return removeDevCartItem(request);
    }
    const cart = await apiRequest<Cart>("/cart/item", {
      method: "DELETE",
      body: JSON.stringify(request),
    });
    return transformCartResponse(cart);
  },
};

/**
 * Checkout API
 */
export const checkoutApi = {
  /**
   * Get shipping options
   */
  getShippingOptions: async (): Promise<{ options: ShippingOption[]; currency: string }> => {
    return apiRequest<{ options: ShippingOption[]; currency: string }>(
      "/checkout/shipping-options"
    );
  },

  /**
   * Create order from cart
   */
  createOrder: async (request: CreateOrderRequest): Promise<CreateOrderResponse> => {
    return apiRequest<CreateOrderResponse>("/checkout", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },
};

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Get order details with items
   */
  getOrder: async (orderId: string): Promise<OrderWithItems> => {
    return apiRequest<OrderWithItems>(`/orders/${orderId}`);
  },
};

/**
 * Customer lookup response type
 */
export interface CustomerLookupResponse {
  found: boolean;
  customer?: {
    name: string | null;
    email: string | null;
  };
  shipping: {
    name: string;
    email: string | null;
    phone: string;
    address: string;
    city: string;
    postal_code: string;
    province: string;
  } | null;
}

/**
 * Customers API
 */
export const customersApi = {
  /**
   * Look up customer by phone number for auto-fill
   */
  lookupByPhone: async (
    phoneNumber: string,
    retailerId: string
  ): Promise<CustomerLookupResponse> => {
    return apiRequest<CustomerLookupResponse>("/customers/lookup", {
      method: "POST",
      body: JSON.stringify({ phone_number: phoneNumber, retailer_id: retailerId }),
    });
  },
};

/**
 * Session response type
 */
export interface SessionResponse {
  session: {
    id: string;
    customer_id: string | null;
    retailer_id: string;
    status: string;
    created_at: string;
    last_activity_at: string;
    context?: {
      attribution?: {
        linkId?: string;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
      };
    };
  };
  cart: {
    item_count: number;
    subtotal_cents: number;
    currency: string;
  };
}

/**
 * Sessions API
 */
export const sessionsApi = {
  /**
   * Get session details including attribution context
   */
  getSession: async (sessionId: string): Promise<SessionResponse> => {
    const response = await apiRequest<SessionResponse>(`/sessions/${sessionId}`);
    return response;
  },
};

/**
 * PostHog Analytics API
 */
export interface TrackAnalyticsRequest {
  session_id: string;
  event: string;
  properties?: Record<string, unknown>;
}

export const analyticsApi = {
  /**
   * Track analytics event via PostHog
   * Uses direct Supabase Edge Function URL
   * Returns true if successful, false otherwise 5c901958-9ea9-494f-b10d-c27d9ddad60d
   */
  track: async (request: TrackAnalyticsRequest): Promise<boolean> => {
    const sessionToken = getSessionToken();
    if (!sessionToken) {
      return false;
    }

    const url = import.meta.env.VITE_API_ANALYTICS;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      [config.sessionTokenHeader]: sessionToken,
    };

    // Add Supabase anon key if configured (required for edge functions)
    if (config.supabaseAnonKey) {
      headers["Authorization"] = `Bearer ${config.supabaseAnonKey}`;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      });
    } catch (error) {
      // Network errors return false
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { api_endpoint: "posthog-analytics", error_type: "network" },
          extra: { url },
        });
      }
      return false;
    }

    // Handle non-2xx responses - return false
    if (!response.ok) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        try {
          const errorData = await response.json();
          Sentry.captureException(new Error("Analytics tracking failed"), {
            tags: { api_endpoint: "posthog-analytics", error_type: "http" },
            extra: { url, status: response.status, errorData },
          });
        } catch {
          Sentry.captureException(new Error("Analytics tracking failed"), {
            tags: { api_endpoint: "posthog-analytics", error_type: "http" },
            extra: { url, status: response.status },
          });
        }
      }
      return false;
    }

    // Parse successful response
    try {
      const data = await response.json();
      return data.success === true;
    } catch (error) {
      // If response parsing fails, return false
      return false;
    }
  },
};
